import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Grid, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

import {
    LoadingBar,
    BreadCrumbs,
    PrimaryButton,
    ChangePasswordModal,
    Tabs,
    ActionBox,
} from "../../components/Common";
import {
    DetailPanel,
    DetailPanelData,
} from "../../components/Common/DetailPanel";
import { Pill } from "../../components/Common/Pill";

import useUser from "../../context/User/useUser";

import { trakrefFontFamily, AppLanguage } from "../../utils";
import { Grey1, toolTipBlue } from "../../color_utils";
import { TranslateW } from "../../Utils/Languages/Translate";
import Routes from "../../routes";
import UserInfo from "../../UserInfo";

import ServiceEvents from "./Table/ServiceEvents";
import "./Profile.css";
import Enums from "../../enums";

const containerSpacing = { direction: "row", spacing: 2 };

const ProfilePage = () => {
    const [userInformation, setUserInformation] = useState({
        id: 0,
        guid: "",
        name: "",
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        state: "",
        stateName: "",
        isReseller: false,
        country: "",
        requireAdminApproval: false,
        certificationType: "0",
        language: "en-US",
        userType: 1,
        userTypeName: "",
        hasOtherInstances: false,
        notificationTypeId: 1,
        weightUnits: "",
        userPermissions: [],
        company: "",
        address1: "",
        address2: "",
        county: "",
        countryName: "",
        title: "",
        city: "",
        zip: "",
        phone: "",
        cell: "",
        fax: "",
        shortName: "",
        fkid: 0,
        fkName: "",
        headshot: "",
    });
    const [loading, setLoading] = useState(true);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [activeTab, setActiveTab] = useState(1);

    const history = useHistory();
    const { logout } = useUser();

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch(Routes.User.GetUserDetails(UserInfo.userId));
            const user = await response.json();

            const newUser = {
                ...user,
                language: AppLanguage.find((x) => x.id === user.language).name,
            };

            setUserInformation({ ...userInformation, ...newUser });
            setLoading(false);
        };

        fetchData();
    }, []);

    const onhandleChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const onPasswordModalState = () => {
        setShowPasswordModal((prev) => !prev);
    };

    const onHandleNavigateEdit = () => {
        history.push("/Profile/Edit");
    };

    if (loading && userInformation.id === 0) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={[{ text: "Your Profile" }]}
            />

            <Grid container {...containerSpacing}>
                <Grid item ml={"14px"} xs={12} md={12}>
                    <div className="profile-header-container">
                        <div className="profile-name-container">
                            {userInformation.headshot ? (
                                <img
                                    className="greenImageSmall"
                                    src={userInformation.headshot}
                                />
                            ) : (
                                <div className="greenCircleSmall">
                                    {userInformation.shortName}
                                </div>
                            )}
                            <Typography
                                variant="h5"
                                sx={{
                                    "&.MuiTypography-root": {
                                        fontFamily: trakrefFontFamily,
                                        fontSize: "22px",
                                    },
                                }}
                                style={{ color: Grey1 }}
                            >
                                {userInformation.name}
                            </Typography>
                            <Pill text={userInformation.userTypeName} bgColor={toolTipBlue} />
                        </div>
                        {UserInfo.isTenantAdministrator() ||
                            UserInfo.isSystemAdministrator() ? (
                            <PrimaryButton
                                buttonText={TranslateW("Profile.Edit")}
                                onClick={onHandleNavigateEdit}
                            />
                        ) : null}
                    </div>
                </Grid>
            </Grid>

            <Grid container {...containerSpacing} mt={0.5}>
                <Grid item xs={10} md={10} lg={10}>
                    <DetailPanel expandable>
                        {!UserInfo.IsWriteIntech && (
                            <DetailPanelData
                                title={TranslateW("Profile.Username")}
                                data={userInformation.userName}
                                link={false}
                            />
                        )}
                        {!UserInfo.IsWriteIntech && (
                            <DetailPanelData
                                title={TranslateW("Profile.EmailAddress")}
                                data={userInformation.email}
                                link={false}
                            />
                        )}
                        <DetailPanelData
                            title={TranslateW("Profile.TechnicianCertificationNumber")}
                            data={""}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Firstname")}
                            data={userInformation.firstName}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Company")}
                            data={userInformation.fkName}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Address1")}
                            data={userInformation.address1}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Lastname")}
                            data={userInformation.lastName}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Title")}
                            data={userInformation.title}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.City")}
                            data={userInformation.city}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Address2")}
                            data={userInformation.address2}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.County")}
                            data={userInformation.county}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Country")}
                            data={userInformation.countryName}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.State")}
                            data={userInformation.stateName}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Zip")}
                            data={userInformation.zip}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Phone")}
                            data={userInformation.phone}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Cell")}
                            data={userInformation.cell}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Fax")}
                            data={userInformation.fax}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Language")}
                            data={userInformation.language}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Weightsin")}
                            data={userInformation.weightUnits}
                            link={false}
                        />
                        <DetailPanelData
                            title={TranslateW("Profile.Role")}
                            data={userInformation.userTypeName}
                            link={false}
                        />
                    </DetailPanel>
                </Grid>
                { UserInfo.getLoginTypeId() === Enums.LoginType.Trakref && (
                    <Grid item xs={2}>
                        <ActionBox
                            items={[{
                                title: TranslateW("Profile.ChangePassword"),
                                Icon: LockIcon,
                                onClick: onPasswordModalState
                            }]}
                            sx={{ height: 'fit-content !important' }}
                        />
                    </Grid>
                )}
            </Grid>

            <div className="p-0 mt-3 col-md-12 f-jb">
                <Tabs
                    activeTab={activeTab}
                    onhandleChange={onhandleChange}
                    labels={[TranslateW("Profile.ServiceEvents")]}
                    tabElements={[<ServiceEvents id={UserInfo.userId} />]}
                    tabCustomStyle={{ ml: "25px" }}
                    tabPanelCustomStyle={{ ml: "8px" }}
                />
            </div>

            <ChangePasswordModal
                modalOpen={showPasswordModal}
                onClose={onPasswordModalState}
                userId={UserInfo.userId}
                onLogoutSuccess={() => setTimeout(async () => await logout(), 4000)}
                isProfile={true}
            />
        </>
    );
};

export default ProfilePage;
